import AppContext from 'App/AppContext';
import { IPSpinner, IPSpinnerSize, IPText } from 'baseComponents';
import { IPLink } from 'baseComponents/Link/IPLink';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { createLibraryItemReadActivity } from 'components/Links/Helpers/LinkReadActivityHelper';
import HrefLink from 'components/Links/HrefLink';
import LinkPreviewModal from 'components/Links/Preview/LinkPreviewModal';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import Entity, { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import { Fragment, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLink } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { getAuditTaskHref } from 'utils/internalAudit';
import { getEntityUrl, navigateToExternalUrl } from 'utils/url';
import { ResourcePanelAuditTasks } from './ResourcePanelAuditTasks';

export interface IAuditScopeItemTarget {
  entityType: number;
  entityId: number;
  code?: string;
  name?: string;
  auditTaskId?: number;
  internalAuditId?: number;
}

interface IAuditScopeItemLinkProps extends IAuditScopeItemTarget {
  prominent?: boolean;
  // called after a document modal that showed the audit task pane closes, so the host can reload
  onPreviewClosed?: () => void;
}

// module-level so the pane's loader dependency stays identity-stable; the modal shows the pane
// whatever the count is
const ignoreAvailability = (): void => undefined;

// One scope item of an audit. Entity families open their own scene in a new tab, with tid/ouid in
// the href so a multi-tenant user opens the right tenant. A document has no scene: it opens in the
// library preview modal with its audit task beside it, whatever the tenant setting for documents.
export const useAuditScopeItemOpener = (onPreviewClosed?: () => void) => {
  const { t } = useTranslation(['audit']);
  const appContext = useContext(AppContext);

  const [previewLink, setPreviewLink] = useState<ResourceLink | undefined>(undefined);
  const [previewTarget, setPreviewTarget] = useState<IAuditScopeItemTarget | undefined>(undefined);
  const [isOpening, setIsOpening] = useState<boolean>(false);
  // the pane's autosave flush, awaited before the host reloads on close
  const flushPaneRef = useRef<(() => Promise<boolean>) | undefined>(undefined);

  const openScopeItem = async (item: IAuditScopeItemTarget) => {
    if (item.entityType !== (EntityTypes.Link as number)) {
      const itemName = `${item.code ?? ''} ${item.name ?? ''}`.trim();
      const url =
        getAuditTaskHref(item.entityType as EntityTypes, item.entityId, item.auditTaskId, item.internalAuditId) ||
        getEntityUrl(new Entity(item.entityId, item.entityType, itemName));
      if (!url) return;

      navigateToExternalUrl(url, appContext.user.tenant, appContext.user.login.tenantId);

      return;
    }

    // the scope read models carry only the link id, so the full link is fetched on click
    try {
      setIsOpening(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const link = await apiGetLink(item.entityId, accessToken, appContext.globalDataCache);
      if (!link?.linkId) return;

      setPreviewTarget({
        entityType: item.entityType,
        entityId: item.entityId,
        auditTaskId: item.auditTaskId,
        internalAuditId: item.internalAuditId,
      });
      setPreviewLink(link);
      void createLibraryItemReadActivity(link, appContext);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsOpening(false);
    }
  };

  // on the phone layout the pane would cover the document, so the modal shows the document only
  const showTaskPane =
    Boolean(previewTarget?.auditTaskId && previewTarget?.internalAuditId) && !appContext.isMobileView;

  const closePreview = () => {
    const flush = showTaskPane ? flushPaneRef.current : undefined;
    flushPaneRef.current = undefined;
    setPreviewLink(undefined);
    setPreviewTarget(undefined);
    if (!showTaskPane) return;

    // the pane changes the task's status and outcome, so the host reloads after the last debounced
    // save has completed
    void (async () => {
      await flush?.();
      onPreviewClosed?.();
    })();
  };

  // handed back rather than rendered here: the modal belongs to whatever opened the item, and the
  // command bar has no cell to hang it in
  const scopeItemPreview = (
    <LinkPreviewModal
      isOpen={previewLink !== undefined}
      onClose={closePreview}
      link={previewLink}
      confirmOpen={false}
      enableLinks={true}
      sidePaneTitle={showTaskPane ? t('audit:Execute.Task.RelatedTasks.Tab') : undefined}
      sidePane={
        showTaskPane && previewTarget ? (
          <ResourcePanelAuditTasks
            key={`${previewTarget.entityId}-${previewTarget.auditTaskId}`}
            entityType={EntityTypes.Link}
            entityId={previewTarget.entityId}
            initialTaskId={previewTarget.auditTaskId}
            initialInternalAuditId={previewTarget.internalAuditId}
            isOpen
            onAvailabilityChange={ignoreAvailability}
            onRegisterFlush={(flush) => {
              flushPaneRef.current = flush;
            }}
            onNavigateToStop={(stop, internalAuditId) =>
              openScopeItem({
                entityType: stop.entityType,
                entityId: stop.entityId,
                auditTaskId: stop.taskId,
                internalAuditId,
              })
            }
          />
        ) : undefined
      }
    />
  );

  return { openScopeItem, isOpening, scopeItemPreview };
};

export const AuditScopeItemLink = (props: IAuditScopeItemLinkProps) => {
  const { openScopeItem, isOpening, scopeItemPreview } = useAuditScopeItemOpener(props.onPreviewClosed);

  const itemName = `${props.code ?? ''} ${props.name ?? ''}`.trim();
  const linkText = itemName;
  const auditTaskHref = getAuditTaskHref(
    props.entityType as EntityTypes,
    props.entityId,
    props.auditTaskId,
    props.internalAuditId,
  );

  if (!linkText) return null;

  if (props.entityType !== EntityTypes.Link) {
    return (
      <IPText
        variant={props.prominent ? 'large' : undefined}
        styles={props.prominent ? globalTextStylesBold : undefined}
      >
        <HrefLink
          entity={auditTaskHref ? undefined : new Entity(props.entityId, props.entityType, itemName)}
          href={auditTaskHref}
          external
          includeContext
          newTab
          underline
        >
          {linkText}
        </HrefLink>
      </IPText>
    );
  }

  return (
    <Fragment>
      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText
            variant={props.prominent ? 'large' : undefined}
            styles={props.prominent ? globalTextStylesBold : undefined}
          >
            <IPLink underline onClick={() => openScopeItem(props)}>
              {linkText}
            </IPLink>
          </IPText>
        </IPStack.Item>
        {isOpening && (
          <IPStack.Item>
            <IPSpinner size={IPSpinnerSize.small} />
          </IPStack.Item>
        )}
      </IPStack>
      {scopeItemPreview}
    </Fragment>
  );
};
